import React, { useState, useEffect } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import PersonalizationEditModal from "./personalizationEditModal";

const GET_STUDENT_PERSONALIZATIONS = gql`
    query GetStudentPersonalizations($studentId: String!) {
        userById(id: $studentId) {
            ... on Student {
                id
                timeplanCustomizations {
                    id
                    fromUnit
                    toUnit
                    content
                    dayId
                }
            }
        }
        schoolYearOverview {
            timeDescriptions {
                index
                startTime
                endTime
            }
            dayNames
            dayShortNames
        }
    }
`;

const ADD_TIMEPLAN_CUSTOMIZATION = gql`
    mutation AddTimeplanCustomization($studentId: String!, $fromUnit: Int!, $toUnit: Int!, $content: String!, $dayId: Int!) {
        addTimeplanCustomization(studentId: $studentId, fromUnit: $fromUnit, toUnit: $toUnit, content: $content, dayId: $dayId) {
            id
            fromUnit
            toUnit
            content
            dayId
        }
    }
`;

export default function StudentPersonalizationsView({ studentUid, onRefreshRequested }) {
    const navigate = useNavigate();
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingCustomization, setEditingCustomization] = useState(null);

    const { data, loading, error, refetch } = useQuery(GET_STUDENT_PERSONALIZATIONS, {
        variables: { studentId: studentUid },
        skip: !studentUid
    });

    const [addCustomization] = useMutation(ADD_TIMEPLAN_CUSTOMIZATION);

    const personalizations = data?.userById?.timeplanCustomizations || [];
    const timeDescriptions = data?.schoolYearOverview?.timeDescriptions || [];
    const dayNames = data?.schoolYearOverview?.dayNames || [];

    const getTimeSlotDisplay = (unitIndex) => {
        const timeSlot = timeDescriptions.find(t => t.index === unitIndex);
        return timeSlot ? `${timeSlot.startTime} - ${timeSlot.endTime}` : `Unit ${unitIndex}`;
    };

    // New: display only startTime bis endTime for a customization range
    const getStartEndDisplay = (fromUnit, toUnit) => {
        const start = timeDescriptions.find(t => t.index === fromUnit)?.startTime;
        const end = timeDescriptions.find(t => t.index === toUnit)?.endTime;
        if (start && end) return `${start} bis ${end}`;
        if (start) return `${start} bis Unit ${toUnit}`;
        if (end) return `Unit ${fromUnit} bis ${end}`;
        return `Unit ${fromUnit} bis Unit ${toUnit}`;
    };

    const handleCreateNew = async () => {
        if (!studentUid) return;

        try {
            // Call mutation and use returned data immediately
            const result = await addCustomization({
                variables: {
                    studentId: studentUid,
                    fromUnit: 0,
                    toUnit: 0,
                    content: "Neue Personalisierung",
                    dayId: 0
                }
            });


            if (result.error) {
                console.error("Error creating customization:", result.error);
                return;
            }
            const created = result?.data?.addTimeplanCustomization;
            if (created) {
                // open modal right away with server-returned customization
                setEditingCustomization(created);
                setShowEditModal(true);

                await refetch(); // Refresh the list when modal closes

                // Inform parent that a refresh is requested
                try { onRefreshRequested && onRefreshRequested(); } catch (e) {}
            }
        } catch (err) {
            console.error("Error creating customization:", err);
        }
    };

    const handleEdit = (customization) => {
        setEditingCustomization(customization);
        setShowEditModal(true);
    };

    const handleModalClose = () => {
        setShowEditModal(false);
        setEditingCustomization(null);
        refetch(); // Refresh the list when modal closes

        // inform parent to refresh teacher week view
        try { onRefreshRequested && onRefreshRequested(); } catch (e) {}
    };

    if (!studentUid) {
        return (
            <div className="my-4">
                <h2 className="text-xl font-semibold mb-2">Personalisierungen</h2>
                <p className="text-gray-600">Bitte wählen Sie einen Schüler aus.</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="my-4">
                <h2 className="text-xl font-semibold mb-2">Personalisierungen</h2>
                <p>Laden...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="my-4">
                <h2 className="text-xl font-semibold mb-2">Personalisierungen</h2>
                <p className="text-red-600">Fehler beim Laden: {error.message}</p>
            </div>
        );
    }

    return (
        <div className="my-4">
            <div className="flex flex-row items-center space-x-2 mb-2 justify-between items-center">
                <h2 className="text-xl font-semibold">Personalisierungen</h2>
                <button
                    onClick={handleCreateNew}
                    className="text-blue-500 hover:underline"
                >
                    Neue hinzufügen
                </button>
            </div>
            <div className="my-2">
                {personalizations.length === 0 ? (
                    <div className="w-full text-center py-8 bg-slate-50 rounded-lg">
                        <h3 className="text-lg font-medium text-slate-800">Keine Personalisierungen</h3>
                        <p className="mt-2 text-sm text-slate-500">
                            Für diesen Schüler sind noch keine Personalisierungen eingerichtet.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="">
                            <div className="space-y-3">
                                {personalizations.map((customization) => (
                                    <div
                                        key={customization.id}
                                        className="flex items-center justify-between p-4 rounded-lg shadow-sm bg-slate-50 border-2 border-slate-200"
                                    >
                                        <div>
                                            <div className="font-semibold text-slate-800">{customization.content}</div>
                                            <div className="text-sm text-slate-500">
                                                {dayNames[customization.dayId] ? `${dayNames[customization.dayId]} · ` : ''}{getStartEndDisplay(customization.fromUnit, customization.toUnit)}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => handleEdit(customization)}
                                            className="text-blue-500 hover:underline text-sm"
                                        >
                                            Bearbeiten
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {showEditModal && editingCustomization && (
                <PersonalizationEditModal
                    customization={editingCustomization}
                    studentId={studentUid}
                    timeDescriptions={timeDescriptions}
                    dayNames={dayNames}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
}